import type { AcceptWorkLogSuggestionDto, AttendanceStatus, WorkLogSuggestion } from '../../types';
import { DURATION_PRESETS } from '../worklogs/durationModel';
import { engagementRequiredOptions, type WorkEngagementOption } from '../timer/timerModel';
import { taskContainerLabel, type EngagementLabelTranslate } from '../../utils/engagementLabel';

/**
 * "תיעוד בלי תירוצים" (documentation brief, parts 1–2, 2026-10-12): the pure
 * rules behind the clock-in nudge, the suggested work logs and "סגירת יום".
 * No React, no i18n; unit-tested in `utils/__tests__/documentationModel.test.ts`.
 */

/** A summary needs at least this many characters (as the work-log form). */
export const MIN_SUMMARY_LENGTH = 5;
/** The accept endpoint takes 1–600 minutes. */
export const MAX_SUGGESTION_MINUTES = 600;
/** "סגירת יום" opens when at least this much attendance is undocumented (owner 2026-10-12). */
export const CLOSE_DAY_GAP_MINUTES = 15;

// ---------------------------------------------------------------------------
// Part 1: clock-in as a duty, gently
// ---------------------------------------------------------------------------

type StatusLike = Pick<AttendanceStatus, 'status' | 'since' | 'today_minutes'> | null | undefined;

/**
 * True when the employee has not clocked in today and has not clocked out
 * today either: no attendance event today at all. The server sends `since`
 * only when there was an event today, and `today_minutes` counts today's work.
 * Unknown (not loaded) is never "needs clock-in".
 */
export function needsClockIn(status: StatusLike): boolean {
  if (!status) return false;
  return status.status === 'clocked_out' && !status.since && !(Number(status.today_minutes) > 0);
}

/** On shift (a break included): the timer can start without asking. */
export function isOnShift(status: StatusLike): boolean {
  return status?.status === 'clocked_in' || status?.status === 'on_break';
}

/** localStorage key of "לא עכשיו": one per user and calendar day, so it lapses tomorrow. */
export function clockInNudgeKey(userId: string, dateKey: string): string {
  return `ote_clock_in_nudge_hidden:${userId}:${dateKey}`;
}

// ---------------------------------------------------------------------------
// Part 2: the gap and "סגירת יום"
// ---------------------------------------------------------------------------

/** Attendance minutes not covered by work logs (never negative). */
export function documentationGap(attendanceMinutes: number | null | undefined, documentedMinutes: number | null | undefined): number {
  const a = Math.round(Number(attendanceMinutes) || 0);
  const d = Math.round(Number(documentedMinutes) || 0);
  return Math.max(0, a - d);
}

export interface CloseDayInput {
  suggestions: number;
  attendanceMinutes: number | null | undefined;
  documentedMinutes: number | null | undefined;
}

/**
 * "סגירת יום" before clocking out: only when there is something to document,
 * i.e. suggestions, or 15+ minutes of attendance without work logs (owner
 * decision 2026-10-12). Otherwise the clock-out runs as it always did.
 */
export function shouldOpenCloseDay({ suggestions, attendanceMinutes, documentedMinutes }: CloseDayInput): boolean {
  if (suggestions > 0) return true;
  return documentationGap(attendanceMinutes, documentedMinutes) >= CLOSE_DAY_GAP_MINUTES;
}

// ---------------------------------------------------------------------------
// One suggestion row
// ---------------------------------------------------------------------------

export interface SuggestionDraft {
  minutes: number;
  summary: string;
  /** The task a meeting without a ticket is logged on ('' = not chosen yet). */
  ticketId: string;
  /** D-299: sent once, with one accept only (never kept in the row's draft) - "yes, I worked in parallel". */
  confirmParallel?: boolean;
}

export type DraftProblem = 'summary' | 'task' | 'minutes';

const clampMinutes = (m: number) => Math.min(MAX_SUGGESTION_MINUTES, Math.max(1, Math.round(m)));

/** The row's starting values: the suggested duration, summary and ticket. */
export function defaultDraft(s: WorkLogSuggestion): SuggestionDraft {
  const m = Number(s.suggested_minutes);
  return {
    minutes: Number.isFinite(m) && m > 0 ? clampMinutes(m) : DURATION_PRESETS[0],
    summary: s.summary ?? '',
    ticketId: s.ticket_id ?? '',
  };
}

/** A meeting with no ticket asks for a task (the employee picks one). */
export function needsTaskPick(s: Pick<WorkLogSuggestion, 'kind' | 'ticket_id'>): boolean {
  return s.kind === 'meeting' && !s.ticket_id;
}

/** Whether the row has duration buttons: an open record's time is already logged in its segments. */
export function hasDurationChoice(s: Pick<WorkLogSuggestion, 'kind'>): boolean {
  return s.kind !== 'open_record';
}

/**
 * The duration buttons: 15 / 30 / 60 / 120 minutes, plus the suggested value
 * when it is not one of them. A suspended timer cannot log more than it ran,
 * so its buttons stop at the suggested (accrued) time.
 */
export function durationChoices(s: Pick<WorkLogSuggestion, 'kind' | 'suggested_minutes'>): number[] {
  if (!hasDurationChoice(s)) return [];
  const raw = Number(s.suggested_minutes);
  const suggested = Number.isFinite(raw) && raw > 0 ? clampMinutes(raw) : null;
  let list: number[] = [...DURATION_PRESETS];
  if (s.kind === 'suspended_timer' && suggested) list = list.filter((m) => m <= suggested);
  if (suggested && !list.includes(suggested)) list.push(suggested);
  return list.sort((a, b) => a - b);
}

/** What stops the row from being accepted as it is, or null. */
export function draftProblem(s: WorkLogSuggestion, d: SuggestionDraft): DraftProblem | null {
  if (needsTaskPick(s) && !d.ticketId) return 'task';
  if (hasDurationChoice(s) && !(d.minutes >= 1 && d.minutes <= MAX_SUGGESTION_MINUTES)) return 'minutes';
  if (d.summary.trim().length < MIN_SUMMARY_LENGTH) return 'summary';
  return null;
}

/** The body of `POST /work-logs/suggestions/accept`. */
export function acceptPayload(s: WorkLogSuggestion, d: SuggestionDraft): AcceptWorkLogSuggestionDto {
  const minutes = hasDurationChoice(s) ? clampMinutes(d.minutes) : clampMinutes(Number(s.suggested_minutes) || 1);
  const body: AcceptWorkLogSuggestionDto = { key: s.key, minutes, summary: d.summary.trim() };
  if (needsTaskPick(s) && d.ticketId) body.ticket_id = d.ticketId;
  if (d.confirmParallel) body.confirm_parallel = true; // D-299 (QX-1302)
  return body;
}

/**
 * BQ-052 (D-121): accepting a suggestion on a task with no engagement - the server answers 409
 * ENGAGEMENT_REQUIRED with the customer's engagements; `ask` shows the picker and the accept is
 * sent again with the chosen one. Null = the employee closed the question (nothing was saved).
 * Any other error goes up as before.
 */
export async function acceptWithEngagement<T>(
  body: AcceptWorkLogSuggestionDto,
  send: (b: AcceptWorkLogSuggestionDto) => Promise<T>,
  ask: (options: WorkEngagementOption[]) => Promise<string | null>
): Promise<T | null> {
  try {
    return await send(body);
  } catch (err) {
    const options = engagementRequiredOptions(err);
    if (options === null) throw err;
    const chosen = await ask(options);
    if (!chosen) return null;
    return send({ ...body, engagement_id: chosen });
  }
}

/** A duration label: "15 דק׳", "2 שע׳" or "1:15 שע׳" (i18n key + value). */
export function durationLabel(minutes: number): { key: 'minutes' | 'hours' | 'hoursMinutes'; value: string | number } {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return { key: 'minutes', value: m };
  if (m % 60 === 0) return { key: 'hours', value: m / 60 };
  return { key: 'hoursMinutes', value: `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}` };
}

/** "8:30" for minutes (hours:minutes, never negative). */
export function hoursMinutes(total: number | null | undefined): string {
  const m = Math.max(0, Math.round(Number(total) || 0));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// What the row says
// ---------------------------------------------------------------------------

/** The meeting's own name: the server puts it on the meeting activity entry. */
function meetingName(s: WorkLogSuggestion): string | null {
  const entry = (s.activity ?? []).find((a) => a.type === 'meeting' && a.label?.trim());
  return entry ? entry.label.trim() : null;
}

/** The row title: the ticket's name, or the meeting's name for a meeting. */
export function suggestionTitle(s: WorkLogSuggestion): string | null {
  if (s.kind === 'meeting') return meetingName(s) ?? s.ticket_title ?? null;
  return s.ticket_title ?? null;
}

/**
 * The second line: customer · project (and the ticket a meeting is linked to).
 * A task with no project shows its engagement instead ("name · type", P-02b).
 */
export function suggestionMeta(s: WorkLogSuggestion, t?: EngagementLabelTranslate): string {
  const linked = s.kind === 'meeting' && meetingName(s) && s.ticket_title ? s.ticket_title : null;
  const container = t ? taskContainerLabel(s, t) : s.project_name || s.engagement_name;
  return [linked, s.organization_name, container].filter((p) => p && String(p).trim()).join(' · ');
}

export interface SuggestionLine {
  /** i18n key under `documentation.line`. */
  key: 'activity' | 'activityMore' | 'meeting' | 'suspended' | 'openRecord';
  params: Record<string, string | number>;
}

/**
 * What happened and when, from `activity` (sorted by time):
 * - ticket activity: "מענה ללקוח · 10:12–10:48" (+ "ועוד 2" when there is more),
 * - meeting: "פגישה · 10:00–11:00",
 * - suspended timer: "הטיימר על X נעצר ב-HH:MM",
 * - open record: "יש X דק׳ על Y בלי סיכום".
 * `clock` formats an ISO time; `duration` formats minutes.
 */
export function describeSuggestion(
  s: WorkLogSuggestion,
  clock: (iso: string) => string,
  duration: (minutes: number) => string,
): SuggestionLine {
  const title = s.ticket_title ?? meetingName(s) ?? '';
  const activity = [...(s.activity ?? [])].filter((a) => a && a.at).sort((a, b) => a.at.localeCompare(b.at));
  const first = activity[0]?.at ?? s.start_time;
  const last = activity[activity.length - 1]?.at ?? s.end_time;

  if (s.kind === 'suspended_timer') {
    return { key: 'suspended', params: { title, time: clock(s.end_time || last) } };
  }
  if (s.kind === 'open_record') {
    return { key: 'openRecord', params: { title, duration: duration(Number(s.suggested_minutes) || 0) } };
  }
  const range = (a: string, b: string) => {
    const x = clock(a);
    const y = clock(b);
    return !y || x === y ? x : `${x}–${y}`;
  };
  if (s.kind === 'meeting') {
    return { key: 'meeting', params: { time: range(s.start_time, s.end_time) } };
  }
  const labels: string[] = [];
  for (const a of activity) {
    const l = a.label?.trim();
    if (l && !labels.includes(l)) labels.push(l);
  }
  const time = range(first, last);
  if (labels.length <= 2) return { key: 'activity', params: { what: labels.join(' · '), time } };
  return { key: 'activityMore', params: { what: labels.slice(0, 2).join(' · '), count: labels.length - 2, time } };
}

/** The outcome of "אישור הכול": how many were saved and how many stayed. */
export function bulkOutcome(results: readonly boolean[]): { accepted: number; failed: number } {
  const accepted = results.filter(Boolean).length;
  return { accepted, failed: results.length - accepted };
}
